import { Component, Input, OnInit, OnChanges, ViewChild, HostListener, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MdDialog, MdDialogRef } from '@angular/material';
import { Router } from '@angular/router';
import { Observable } from 'rxjs/Observable';

import { ConstantsService } from 'app/services/constants-service';
import { MessageService } from 'app/services/messages.service';
import { SnackBarService } from 'app/services/snackbar.service';
import { GlobalValuesService } from 'app/services/global-values.service';
import { ApplicationValue } from 'app/services/warmup/application-type.model';
import { WarmUpService, ApplicationTypeConstants } from 'app/services/warmup/warmup.service';
import { TrackingService } from 'app/shared/services/tracking/google-analytics-events.service';
import { TrackingConstants } from 'app/shared/services/tracking/tracking-constants.service';
import { unsavedFormCheck } from 'app/shared/validators/generic-validators';

import { GoogleSearchResultsComponent } from '../googlesearchresults/google-search-results.component';

import { CreatePersonService } from './create-person.service';

import { Person } from 'app/shared/models/person.model';

@Component({
  selector: 'create-organisation',
  providers: [CreatePersonService],
  templateUrl: './create-person.component.html'
})

/**
 * Component to add new Person.
 */
export class CreatePersonComponent implements OnInit, OnChanges {

  @Input() person: Person;

  createPersonForm: FormGroup;
  firstName: string;
  gender: ApplicationValue[];
  nationality: ApplicationValue[];
  nationalityDropDownValues: any[] = [];
  selectedIndex: 0;
  savingPerson = false;
  redirectAfterCreate = false;

  @ViewChild(GoogleSearchResultsComponent)
  private searchComponent: GoogleSearchResultsComponent;

  formErrors = {
    'firstname': '',
    'callingname': '',
    'lastname': '',
    'gender': '',
    'nationality': '',
  };

  validationMessages = {
    'firstname': {
      'required': 'Required field value.',
    },
    'callingname': {
      'required': 'Required field value.',
    },
    'lastname': {
      'required': 'Required field value.',
    },
    'gender': {
      'required': 'Required field value.',
    },
    'nationality': {
      'required': 'Required field value.',
    },
  };

  constructor(private formBuilder: FormBuilder,
    private createPersonService: CreatePersonService,
    private warmUpService: WarmUpService,
    private snackBarService: SnackBarService,
    private dialogRef: MdDialogRef<CreatePersonComponent>,
    private cdr: ChangeDetectorRef,
    private dialog: MdDialog,
    private globalValuesService: GlobalValuesService,
    private router: Router,
    private trackingService: TrackingService) { }

  ngOnInit(): void {
    this.buildForm();
    this.setUpGender(this.warmUpService.getApplicationValue(ApplicationTypeConstants.GENDER_TYPE));
    this.setUpNationality(this.warmUpService.getApplicationValue(ApplicationTypeConstants.COUNTRY_TYPE));
    const fullNameArray = this.firstName.split(' ');
    this.createPersonForm.get('firstname').setValue(fullNameArray[0]);
    if (fullNameArray.length > 2) {
      this.createPersonForm.get('middlename').setValue(fullNameArray[1]);
      this.createPersonForm.get('lastname').setValue(fullNameArray[2]);
    } else if (fullNameArray.length > 1) {
      this.createPersonForm.get('lastname').setValue(fullNameArray[1]);
    }
  }

  ngOnChanges() {
    this.createPersonForm.reset({
      // n.b. discrepancies between names.
      callingname: this.person.callingName,
      firstname: this.person.firstNames,
      middlename: this.person.middleNames,
      lastname: this.person.lastNames,
      gender: this.person.gender,
      dateofbirth: this.person.birthday,
      nationality: this.person.nationality,
      ssn: this.person.ssn,
    });
  }

  buildForm() {
    this.createPersonForm = this.formBuilder.group({
      callingname: ['', Validators.required],
      firstname: ['', Validators.required],
      middlename: '',
      lastname: ['', Validators.required],
      gender: ['', Validators.required],
      dateofbirth: '',
      nationality: ['', Validators.required],
      ssn: '',
    });

    this.createPersonForm.valueChanges.subscribe(data => this.onValueChanged(data));
    this.onValueChanged(); // (re)set validation messages now
  }

  onValueChanged(data?: any) {
    if (!this.createPersonForm) {
      return;
    }
    const form = this.createPersonForm;

    for (const field in this.formErrors) {
      // clear previous error message (if any)
      this.formErrors[field] = '';
      const control = form.get(field);
      if (control && control.touched && !control.valid) {
        const messages = this.validationMessages[field];
        for (const key in control.errors) {
          this.formErrors[field] += messages[key] + ' ';
        }
      }
    }
  }

  onSubmit({ value, valid }: { value: Person, valid: boolean }) {
    this.savingPerson = true;
    this.createPersonService.addPerson(value)
      .subscribe(
      success => {
        this.savingPerson = false;
        this.callSnackBarService(success);
        if (this.redirectAfterCreate) {
          this.router.navigate(['/entity', success.arrResult.entityId, 'details']);
        }
        this.dialogRef.close(success.arrResult);
        this.trackingService
          .emitEvent(TrackingConstants.CREATE_PERSON, TrackingConstants.SAVE_PERSON, TrackingConstants.SUCCESS, 1);
      },
      err => {
        this.savingPerson = false;
        this.callSnackBarService(err);
        this.trackingService
          .emitEvent(TrackingConstants.CREATE_PERSON, TrackingConstants.SAVE_PERSON, TrackingConstants.FAILURE, 0);
      }
      );
  }


  setUpGender(dropDownValues: any) {
    if (dropDownValues instanceof Observable) {
      dropDownValues.subscribe(
        response => {
          this.warmUpService.setApplicationTypeInLS(response);
          this.gender = response.values;
          this.cdr.detectChanges();
        }
      );
    } else {
      this.gender = dropDownValues;
    }
  }

  setUpNationality(dropDownValues: any) {
    if (dropDownValues instanceof Observable) {
      dropDownValues.subscribe(
        response => {
          this.warmUpService.setApplicationTypeInLS(response);
          this.nationality = response.values;
          this.cdr.detectChanges();
        }
      );
    } else {
      this.nationality = dropDownValues;
    }
    // TODO(naveenkiran): Adding default entry to the dropdown list as placeholder value is
    // not getting set. Remove this once we use dropdown component of ng-material library.
    this.nationalityDropDownValues.push(
      {
        'value': '',
        'label': 'Select country *'  // >> most simple fix to show 'required' in p-dropdown.
      });
    for (let countryKey in this.nationality) {
      let countryRecord = this.nationality[countryKey];
      this.nationalityDropDownValues.push(
        {
          'value': countryRecord.id,
          'label': countryRecord.name
        });
    }
  }

  callSnackBarService(response: any) {
    this.snackBarService
      .showStatus(response,
      MessageService.CREATE_PERSON_SUCCESS,
      MessageService.CREATE_PERSON_FAILURE);
  }

  selectedIndexChange(tabIndex) {
    this.selectedIndex = tabIndex;
    const searchText = this.createPersonForm.get('firstname').value + ' ' + this.createPersonForm.get('lastname').value;
    this.searchComponent.setSearchText(searchText);
    this.searchComponent.loadData(null);
  }

  @HostListener('keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    unsavedFormCheck(event, this.dialogRef, this.createPersonForm);
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/components/createperson/create-person.component.ts